import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Trash2, Plus, Minus, PlusCircle, Sparkles, MessageSquare } from "lucide-react";
import { useOrder } from "@/store/orderStore";
import { useTenant } from "@/tenant/TenantProvider";
import { EVENTS, track } from "@/lib/analytics";
import { formatPrice, formatMacros, sumMacros, lineItemPrice } from "@/lib/format";

// Deterministic upsell picker — recommends 2-3 items NOT already in the cart.
const pickUpsells = (cart, findDish) => {
  const inCart = new Set(cart.map((it) => it.dishId));
  const cats = new Set(cart.map((it) => findDish(it.dishId)?.category).filter(Boolean));
  const suggestions = [];

  const push = (dishId) => {
    if (inCart.has(dishId)) return;
    if (suggestions.find((s) => s.id === dishId)) return;
    const d = findDish(dishId);
    // Never upsell something the kitchen has stop-listed.
    if (d && d.available !== false) suggestions.push(d);
  };

  if (!cats.has("drinks")) {
    const hasHeavyProtein = cart.some((it) => {
      const d = findDish(it.dishId);
      return d && (d.category === "grass_fed" || d.category === "grain_fed" || d.category === "protein");
    });
    if (hasHeavyProtein) {
      push("coco_electro");
      push("butter_energy");
    } else {
      push("butter_green");
      push("protein_latte");
    }
  }
  const hasProtein = cart.some((it) => {
    const d = findDish(it.dishId);
    return d && (d.category === "grass_fed" || d.category === "grain_fed" || d.category === "protein");
  });
  if (hasProtein && !cats.has("sauces")) push("chimichurri");
  if (!cats.has("fruit") && suggestions.length < 3) push("watermelon");

  return suggestions.slice(0, 3);
};

// The values themselves come from the dish document and stay as the kitchen
// knows them; only the word around the milk choice is ours to translate.
const optionsLabel = (t, opts) =>
  [
    opts?.variant,
    opts?.doneness,
    opts?.style,
    opts?.milk && opts.milk !== "Regular" ? t("cart.milkOption", { milk: opts.milk }) : null,
    opts?.note,
  ]
    .filter(Boolean)
    .join(" · ");

const Cart = ({ onBack, onPlace, onAddMore, onAuto }) => {
  const { state, addToCart, decrementCart, removeCart, clearCart, setOrderNote } = useOrder();
  const { findDish, taxRate, serviceRate, restaurant } = useTenant();
  const { t } = useTranslation();
  const cart = state.cart;
  const aiEnabled = restaurant?.ai?.enabled !== false;

  // Display-only running total. The server re-prices the order on placement and
  // the confirmation screen shows what it returns.
  const totals = useMemo(() => sumMacros(cart, findDish), [cart, findDish]);
  const upsells = useMemo(() => pickUpsells(cart, findDish), [cart, findDish]);
  const tax = totals.price * taxRate;
  const service = totals.price * serviceRate;
  const grand = totals.price + tax + service;

  const handleClear = () => {
    if (cart.length === 0) return;
    // Simple confirm; keeps footprint small.
    if (window.confirm(t("cart.clearConfirm"))) clearCart();
  };

  return (
    <div className="pb-40">
      {/* Header: back + title + Clear cart (top right, red underlined) */}
      <div className="px-5 pt-6 pb-3 flex items-center gap-3">
        <button
          data-testid="cart-back"
          onClick={onBack}
          className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center shrink-0"
          aria-label={t("common.back")}
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500">
            {t("cart.overline")}
          </div>
          <div className="text-base font-bold text-neutral-900 -mt-0.5 truncate">
            {t("cart.title", { table: state.guest.table })}
          </div>
        </div>
        {cart.length > 0 && (
          <button
            data-testid="cart-clear"
            onClick={handleClear}
            className="text-[13px] font-bold text-red-600 underline decoration-red-500 decoration-2 underline-offset-[3px] active:opacity-70 shrink-0 text-right"
          >
            {t("cart.clear")}
          </button>
        )}
      </div>

      {cart.length === 0 ? (
        <div className="px-5 py-20 text-center">
          <div className="text-5xl mb-4">🍽️</div>
          <h3 className="text-xl font-black text-neutral-900">{t("cart.emptyTitle")}</h3>
          <p className="text-sm text-neutral-500 mt-2">{t("cart.emptyBody")}</p>
          {/* An empty cart is a guest who has not decided. The AI was reachable
              only from the home screen, so the way out of this screen led back
              to the same menu they had just left. */}
          <div className="mt-6 flex flex-col items-center gap-2">
            {onAuto && aiEnabled && (
              <button
                data-testid="cart-empty-auto"
                onClick={onAuto}
                className="px-6 h-12 rounded-2xl bg-[#E34A27] text-white font-bold text-sm flex items-center gap-2 active:scale-[0.98] transition-transform"
              >
                <Sparkles size={16} /> {t("cart.emptyAuto")}
              </button>
            )}
            <button
              data-testid="cart-empty-back"
              onClick={onBack}
              className="px-6 h-12 rounded-2xl bg-neutral-900 text-white font-bold text-sm"
            >
              {t("cart.backToMenu")}
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* TOTAL SUMMARY — moved to TOP */}
          <div className="px-5 mt-1">
            <div
              data-testid="cart-totals-top"
              className="bg-neutral-900 text-white rounded-2xl p-5 relative overflow-hidden"
            >
              <div
                className="absolute inset-0 opacity-[0.06] pointer-events-none"
                style={{ backgroundImage: "radial-gradient(rgba(255,255,255,1) 1px, transparent 1px)", backgroundSize: "5px 5px" }}
              />
              <div className="relative">
                <div className="flex items-center justify-between gap-2 text-[10px] font-mono uppercase tracking-wider text-white/60">
                  <span>{t("cart.totalMacros")}</span>
                  <span className="text-right">{formatMacros(totals)}</span>
                </div>
                <div className="h-px bg-white/10 my-3" />
                <div className="flex items-end justify-between gap-3">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/60">
                    {t("cart.totalToPay")}
                  </span>
                  <div className="text-right shrink-0">
                    <span data-testid="cart-total" className="text-3xl font-black tracking-tight">
                      {formatPrice(grand)}{" "}
                      <span className="text-sm font-bold text-white/60">IDR</span>
                    </span>
                    <div className="text-[10px] text-white/50 mt-0.5">
                      {t("cart.inclusive", {
                        tax: Math.round(taxRate * 100),
                        service: Math.round(serviceRate * 100),
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CART ITEMS */}
          <div className="px-5 mt-4 space-y-2">
            {cart.map((it) => {
              const dish = findDish(it.dishId);
              if (!dish) return null;
              const lineTotal = lineItemPrice(dish, it.options, it.qty);
              return (
                <div
                  key={it.uid}
                  data-testid={`cart-item-${it.dishId}`}
                  className="bg-white border border-neutral-100 rounded-2xl p-4 flex items-start gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[15px] text-neutral-900">{dish.name}</div>
                    {optionsLabel(t, it.options) && (
                      <div className="text-[11px] text-neutral-500 mt-0.5 italic">
                        {optionsLabel(t, it.options)}
                      </div>
                    )}
                    <div className="text-[11px] font-mono text-neutral-500 mt-1.5">
                      {dish.p}P · {dish.f}F · {dish.c}C · {dish.kcal} kcal
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`cart-dec-${it.uid}`}
                          onClick={() => decrementCart(it.uid)}
                          className="w-8 h-8 rounded-full border border-neutral-300 flex items-center justify-center"
                          aria-label={t("common.decrease")}
                        >
                          <Minus size={14} />
                        </button>
                        <span className="text-sm font-bold w-5 text-center">{it.qty}</span>
                        <button
                          data-testid={`cart-inc-${it.uid}`}
                          onClick={() => addToCart(it.dishId, it.options, it.source || "manual")}
                          className="w-8 h-8 rounded-full bg-neutral-900 text-white flex items-center justify-center"
                          aria-label={t("common.increase")}
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <div className="text-base font-black text-[#E34A27]">{formatPrice(lineTotal)}</div>
                    </div>
                  </div>
                  <button
                    data-testid={`cart-remove-${it.uid}`}
                    onClick={() => removeCart(it.uid)}
                    className="w-9 h-9 rounded-full bg-neutral-50 text-neutral-500 flex items-center justify-center shrink-0"
                    aria-label={t("common.remove")}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              );
            })}
          </div>

          {/* ADD MORE + COMMENT */}
          {onAddMore && (
            <div className="px-5 mt-3">
              <button
                data-testid="cart-add-more"
                onClick={onAddMore}
                className="w-full min-h-12 py-2 px-3 rounded-2xl bg-white border-2 border-dashed border-neutral-300 text-neutral-700 font-bold text-sm flex items-center justify-center gap-2 text-center active:scale-[0.98] transition-transform hover:border-neutral-900 hover:text-neutral-900"
              >
                <PlusCircle size={16} className="shrink-0" /> {t("cart.addMore")}
              </button>
            </div>
          )}

          {/* amp-block: a note for the kitchen is the guest's own words, and
              often an allergy said again in their own way. */}
          <div className="px-5 mt-3 amp-block">
            <label className="text-[10px] font-black uppercase tracking-[0.15em] text-neutral-500 px-1 flex items-center gap-1.5">
              <MessageSquare size={11} className="shrink-0" /> {t("cart.noteLabel")}
            </label>
            <textarea
              data-testid="cart-order-note"
              value={state.orderNote || ""}
              onChange={(e) => setOrderNote(e.target.value.slice(0, 300))}
              rows={3}
              placeholder={t("cart.notePlaceholder")}
              className="mt-1.5 w-full bg-white border border-neutral-200 rounded-2xl p-3 text-sm text-neutral-900 outline-none focus:border-neutral-900 transition-colors resize-none"
            />
            <div className="text-[10px] text-neutral-400 mt-1 px-1 text-right">
              {(state.orderNote || "").length}/300
            </div>
          </div>

          {/* UPSELL */}
          {upsells.length > 0 && (
            <div className="px-5 mt-5" data-testid="cart-upsell">
              <div className="flex items-center gap-1.5 mb-2 px-1">
                <Sparkles size={13} className="text-[#E34A27] shrink-0" />
                <div className="text-[10px] font-black uppercase tracking-[0.15em] text-neutral-500">
                  {t("cart.upsellTitle")}
                </div>
              </div>
              <div className="space-y-2">
                {upsells.map((d) => (
                  <div
                    key={d.id}
                    data-testid={`upsell-${d.id}`}
                    className="bg-white border border-neutral-100 rounded-2xl p-3 flex items-center gap-3"
                  >
                    <div className="w-11 h-11 rounded-xl bg-neutral-50 flex items-center justify-center text-xl shrink-0">
                      {d.category === "drinks" ? "🥤" : d.category === "sauces" ? "🫙" : d.category === "fruit" ? "🍉" : "🍽️"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-bold text-neutral-900 truncate">{d.name}</div>
                      <div className="text-[11px] text-neutral-500 mt-0.5">
                        {d.kcal > 0 && <>{d.kcal} kcal · </>}
                        <span className="font-bold text-[#E34A27]">{formatPrice(d.price)}</span>
                      </div>
                    </div>
                    <button
                      data-testid={`upsell-add-${d.id}`}
                      onClick={() => {
                        if (d.milk || d.variant) {
                          // For items needing options, fall back to going through menu.
                          onAddMore && onAddMore();
                          return;
                        }
                        addToCart(d.id);
                      }}
                      className="h-9 px-3 rounded-full bg-neutral-900 text-white text-[11px] font-bold flex items-center gap-1 shrink-0 active:scale-95 transition-transform"
                    >
                      <Plus size={12} /> {t("common.add")}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STICKY PLACE ORDER */}
          <div className="fixed bottom-0 inset-x-0 z-40 pointer-events-none">
            <div className="mx-auto max-w-md px-5 pb-5 pointer-events-auto">
              <button
                data-testid="place-order-btn"
                onClick={() => {
                  // The tap, not the sale. The order is not placed until the
                  // server has priced and stored it, and that is a different
                  // event sent from a different machine — this one is here so
                  // the gap between the two can be seen at all.
                  track(EVENTS.ORDER_SUBMITTED, {
                    items: cart.reduce((n, it) => n + (it.qty || 0), 0),
                    lines: cart.length,
                    ai_lines: cart.filter((it) => it.source === "ai").length,
                    // What the phone thinks it will cost. The server decides.
                    grand_estimate: grand,
                    has_note: Boolean(state.orderNote),
                    fulfillment: state.guest.fulfillment,
                    is_reorder: Boolean(state.reorderOf),
                  });
                  onPlace({
                    items: cart,
                    totals,
                    tax,
                    service,
                    grand,
                    guest: state.guest,
                    orderNote: state.orderNote || "",
                    aiRequestId: state.aiRequestId || null,
                    reorderOf: state.reorderOf || null,
                    placedAt: new Date().toISOString(),
                  });
                }}
                className="w-full h-16 px-4 rounded-2xl bg-[#E34A27] text-white font-black text-lg tracking-wide active:scale-[0.98] transition-transform shadow-2xl"
              >
                {t("cart.placeOrder", { price: formatPrice(grand) })}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Cart;
